import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import UsersService from '../../services/UserService';
import Notifications from '../../services/Notifications';
import Authorize from '../Authorize';
import CustomerSelector from '../CustomerSelector';
import UserHelper from '../../helpers/UserHelper';
import Loading from '../Loading';
import StorefrontPersona from './StorefrontPersona';
import { withStorefrontConfig } from '../../hooks/StorefrontSettingsContext';

const Create = ({ currentUser, customers, personaList, callback, StorefrontConfig, defaultStorefront }) => {
    const localized = StorefrontConfig.labels;
    const initialStorefront = defaultStorefront || StorefrontConfig.getStorefrontByNumber(currentUser.storefrontNumber);

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        selectedPersona: null,
        selectedCustomer: null,
        selectedStorefront: { value: initialStorefront.id, label: initialStorefront.displayName },
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const updateStorefrontPersona = (storefront, persona) => {
        setForm((prev) => ({
            ...prev,
            selectedStorefront: storefront,
            selectedPersona: persona,
        }));
    };

    const updateSelectedCustomer = (customer) => {
        setForm((prev) => ({ ...prev, selectedCustomer: customer }));
    };

    const validate = () => {
        const newErrors = {};
        let isValid = true;

        if (!form.firstName) {
            newErrors.firstName = true;
            isValid = false;
        }

        if (!form.email) {
            newErrors.email = true;
            isValid = false;
        } else if (!UserHelper.isValidEmail(form.email)) {
            newErrors.email = true;
            Notifications.error(localized.InvalidEmailMessage);
            isValid = false;
        }

        if (!form.selectedStorefront?.value) {
            newErrors.selectedStorefront = true;
            isValid = false;
        }

        if (!form.selectedPersona?.value) {
            newErrors.selectedPersona = true;
            isValid = false;
        }

        if (currentUser.role === 'Customer_Administrator' && !form.selectedCustomer) {
            Notifications.error(localized.SelectInitialCustomerMessage);
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const clearForm = () => {
        const resetStorefront = StorefrontConfig.getStorefrontByNumber(currentUser.storefrontNumber);
        setForm({
            firstName: '',
            lastName: '',
            email: '',
            selectedPersona: null,
            selectedCustomer: null,
            selectedStorefront: { value: resetStorefront.id, label: resetStorefront.displayName },
        });
        setErrors({});
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const storefront = StorefrontConfig.getStorefrontById(form.selectedStorefront?.value);
        const user = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            persona: {
                id: form.selectedPersona?.value,
                name: form.selectedPersona?.label,
            },
            storefrontId: storefront?.id,
            storefrontNumber: storefront?.number,
            isActive: true,
        };

        try {
            setIsSubmitting(true);
            const response = await UsersService.add(user, form.selectedCustomer, callback);
            setIsSubmitting(false);

            if (response) {
                const result = JSON.parse(response);
                if (result.IsSuccess) {
                    Notifications.message(localized.UserCreatedSuccess);
                    clearForm();
                } else if (result.storefront) {
                    const storefrontExist = StorefrontConfig.getStorefrontById(result.storefront);
                    Notifications.error(localized.UserExists + storefrontExist.displayName);
                } else {
                    Notifications.error(localized.UserCreatedFailed);
                }
            }
        } catch (error) {
            setIsSubmitting(false);
            Notifications.error(localized.UserCreatedFailed, error);
        }
    };

    if (!StorefrontConfig.storefrontSettingsLoaded) {
        return <Loading type="brand" />;
    }

    return (
        <div className="fele-user-create active">
            <style>{`.fele-user-create .error-input { border: 1px solid red !important; }`}</style>
            <div className="form-wrapper">
                <div className="title">{localized.NewUserTitle}</div>
                <div className="form">
                    <div className="control-wrapper">
                        <div className="label">{localized.FirstName}</div>
                        <div className="input-wrapper">
                            <input
                                name="firstName"
                                type="text"
                                value={form.firstName}
                                onChange={handleChange}
                                className={errors.firstName ? 'error-input' : ''}
                                required
                            />
                        </div>
                    </div>

                    <div className="control-wrapper">
                        <div className="label">{localized.LastName}</div>
                        <div className="input-wrapper">
                            <input
                                name="lastName"
                                type="text"
                                value={form.lastName}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="control-wrapper">
                        <div className="label">{localized.EmailLabel}</div>
                        <div className="input-wrapper">
                            <input
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                className={errors.email ? 'error-input' : ''}
                                required
                            />
                        </div>
                    </div>

                    <div className="control-wrapper">
                        <StorefrontPersona
                            updateStorefrontPersona={updateStorefrontPersona}
                            currentUser={currentUser}
                            errors={errors}
                        />
                    </div>

                    <Authorize as={['CustomerAdministrator']} displayMessage={false}>
                        <div className="control-wrapper">
                            <div className="label">Select Initial Customer</div>
                            <div className="shipto">
                                <CustomerSelector
                                    showLabel={false}
                                    customers={customers}
                                    addCustomer={true}
                                    selectedCustomer={form.selectedCustomer}
                                    updateSelectedCustomer={updateSelectedCustomer}
                                />
                            </div>
                        </div>
                    </Authorize>

                    <div className="button-wrapper">
                        {isSubmitting ? (
                            <Loading />
                        ) : (
                            <button name="addUser" className="button submit" onClick={handleSubmit}>
                                + {localized.ButtonText_AddUser}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

Create.propTypes = {
    currentUser: PropTypes.object.isRequired,
    customers: PropTypes.array,
    personaList: PropTypes.array,
    callback: PropTypes.func,
    StorefrontConfig: PropTypes.object.isRequired,
};

export default withStorefrontConfig(Create);